import type { AnswerStats, BaseStats, DemographicsStats, QuestionStats, SurveyStats } from 'src/redux/survey/survey.types';
import { SurveyQuestionType } from 'src/redux/survey/survey.types';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';

export type RespondentScope = 'all' | 'ai' | 'human';

export const MIN_SEGMENT_N = 5;

export interface BreakoutSegment {
  key: string;
  label: string;
  n: number;
  suppressed: boolean;
}

export interface BreakoutRow {
  answerId: string;
  answerLabel: string;
  answerImageUrl?: string;
  [segmentKey: string]: string | number | undefined;
}

export interface BreakoutModel {
  attributeKey: string;
  attributeLabel: string;
  segments: BreakoutSegment[];
  rows: BreakoutRow[];
  hasData: boolean;
  skipReason?: 'freeform' | 'no-attribute-data';
}

export const DEMOGRAPHIC_LABELS: Record<string, string> = {
  age: 'Age',
  age_group: 'Age',
  gender: 'Gender',
  hh_income: 'Household income',
  household_income: 'Household income',
  education: 'Education',
  ethnicity: 'Ethnicity',
  region: 'Region',
  state: 'State',
  dma: 'DMA',
  occupation_category: 'Occupation',
  marital_status: 'Marital status',
  children: 'Children',
};

const PREFERRED_ATTRIBUTE_ORDER = [
  'age_group',
  'gender',
  'hh_income',
  'education',
  'ethnicity',
  'region',
  'state',
  'dma',
  'occupation_category',
  'marital_status',
  'children',
] as const;

const ATTRIBUTE_ALIASES: Record<string, string> = {
  age: 'age_group',
  household_income: 'hh_income',
};

export const AGE_GROUP_LABELS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'] as const;

const AGE_GROUP_BOUNDS: Array<{ label: typeof AGE_GROUP_LABELS[number]; min: number; max: number }> = [
  { label: '18-24', min: 18, max: 24 },
  { label: '25-34', min: 25, max: 34 },
  { label: '35-44', min: 35, max: 44 },
  { label: '45-54', min: 45, max: 54 },
  { label: '55-64', min: 55, max: 64 },
  { label: '65+', min: 65, max: 200 },
];

function titleCaseCodename(codename: string): string {
  return codename
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function demographicAttributeLabel(attributeKey: string): string {
  return DEMOGRAPHIC_LABELS[attributeKey] ?? titleCaseCodename(attributeKey);
}

function canonicalAttributeKey(attributeKey: string): string {
  return ATTRIBUTE_ALIASES[attributeKey] ?? attributeKey;
}

export function canonicalBreakoutAttribute(attributeKey: string | null): string | null {
  if (!attributeKey) {
    return null;
  }
  return canonicalAttributeKey(attributeKey);
}

function isAgeAttribute(attributeKey: string): boolean {
  return canonicalAttributeKey(attributeKey) === 'age_group';
}

function ageBucketForYears(age: number): typeof AGE_GROUP_LABELS[number] | null {
  if (!Number.isFinite(age) || age < 18) {
    return null;
  }
  const bucket = AGE_GROUP_BOUNDS.find((bound) => age >= bound.min && age <= bound.max);
  return bucket?.label ?? '65+';
}

export function canonicalAgeGroup(raw: string): string {
  const compact = raw
    .trim()
    .toLowerCase()
    .replace(/years?\s*old/g, '')
    .replace(/years?/g, '')
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');

  const exact: Record<string, typeof AGE_GROUP_LABELS[number]> = {
    '18-24': '18-24',
    '25-34': '25-34',
    '35-44': '35-44',
    '45-54': '45-54',
    '55-64': '55-64',
    '65+': '65+',
    '65plus': '65+',
    '65-plus': '65+',
  };
  if (exact[compact]) {
    return exact[compact];
  }

  const range = compact.match(/^(\d{1,3})-(\d{1,3})$/);
  if (range) {
    return ageBucketForYears(Number(range[1])) ?? raw.trim();
  }

  const bare = compact.match(/^(\d{1,3})\+?$/);
  if (bare) {
    return ageBucketForYears(Number(bare[1])) ?? raw.trim();
  }

  return raw.trim();
}

function keysForAttribute(attributeKey: string): string[] {
  const canonical = canonicalAttributeKey(attributeKey);
  if (canonical === 'age_group') {
    return ['age_group', 'age'];
  }
  if (canonical === 'hh_income') {
    return ['hh_income', 'household_income'];
  }
  return [attributeKey];
}

function normalisedSegmentLabel(attributeKey: string, segment: BaseStats): string {
  const raw = segmentLabel(segment);
  return isAgeAttribute(attributeKey) ? canonicalAgeGroup(raw) : raw;
}

function mergeCountedSegments(attributeKey: string, segments: BaseStats[]): BaseStats[] {
  const merged = new Map<string, BaseStats>();
  segments.forEach((segment) => {
    const label = normalisedSegmentLabel(attributeKey, segment);
    const existing = merged.get(label);
    if (!existing) {
      merged.set(label, {
        ...segment,
        title: label,
        displayName: label,
      });
      return;
    }
    existing.totalUsers += segment.totalUsers;
    existing.aiUsers += segment.aiUsers;
    existing.humanUsers += segment.humanUsers;
    existing.rtrUsers += segment.rtrUsers;
  });
  return [...merged.values()];
}

function scopeCount(stats: BaseStats, scope: RespondentScope): number {
  switch (scope) {
    case 'ai':
      return stats.aiUsers;
    case 'human':
      return stats.humanUsers;
    default:
      return stats.totalUsers;
  }
}

function segmentLabel(segment: BaseStats): string {
  return segment.displayName || segment.title;
}

function segmentKeyFromLabel(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return slug || 'segment';
}

function answerList(question: QuestionStats): AnswerStats[] {
  if (Array.isArray(question.answers)) {
    return question.answers;
  }
  if (question.answers && typeof question.answers === 'object') {
    return Object.values(question.answers);
  }
  return [];
}

function answerDemographicSegments(
  answer: AnswerStats,
  attributeKey: string
): BaseStats[] {
  const demographics = answer.demographics as DemographicsStats | Record<string, BaseStats[] | Record<string, BaseStats>> | undefined;
  if (!demographics) {
    return [];
  }

  const collected: BaseStats[] = [];
  keysForAttribute(attributeKey).forEach((key) => {
    const segments = (demographics as Record<string, unknown>)[key];
    if (Array.isArray(segments)) {
      collected.push(...segments);
      return;
    }
    if (segments && typeof segments === 'object') {
      collected.push(...Object.values(segments as Record<string, BaseStats>));
    }
  });
  return mergeCountedSegments(attributeKey, collected);
}

function collectAttributeKeys(stats: SurveyStats): Set<string> {
  const keys = new Set<string>();
  stats.questions.forEach((question) => {
    if (question.demographics) {
      Object.keys(question.demographics).forEach((key) => keys.add(canonicalAttributeKey(key)));
    }
    answerList(question).forEach((answer) => {
      if (!answer.demographics) {
        return;
      }
      Object.keys(answer.demographics).forEach((key) => keys.add(canonicalAttributeKey(key)));
    });
  });
  return keys;
}

function distinctNonZeroSegmentLabels(
  stats: SurveyStats,
  attributeKey: string,
  scope: RespondentScope
): Set<string> {
  const labels = new Set<string>();
  stats.questions.forEach((question) => {
    answerList(question).forEach((answer) => {
      answerDemographicSegments(answer, attributeKey).forEach((segment) => {
        if (scopeCount(segment, scope) > 0) {
          labels.add(normalisedSegmentLabel(attributeKey, segment));
        }
      });
    });
  });
  return labels;
}

function compareAttributeOrder(a: string, b: string): number {
  const canonicalA = canonicalAttributeKey(a);
  const canonicalB = canonicalAttributeKey(b);
  const indexA = PREFERRED_ATTRIBUTE_ORDER.indexOf(canonicalA as typeof PREFERRED_ATTRIBUTE_ORDER[number]);
  const indexB = PREFERRED_ATTRIBUTE_ORDER.indexOf(canonicalB as typeof PREFERRED_ATTRIBUTE_ORDER[number]);
  const rankA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
  const rankB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
  if (rankA !== rankB) {
    return rankA - rankB;
  }
  return demographicAttributeLabel(a).localeCompare(demographicAttributeLabel(b));
}

export function listSurveyBreakoutSegments(
  stats: SurveyStats | undefined,
  attributeKey: string,
  scope: RespondentScope
): Array<{ label: string; n: number }> {
  if (!stats) {
    return [];
  }

  const maxNByLabel = new Map<string, number>();
  stats.questions.forEach((question) => {
    const questionTotals = new Map<string, number>();
    answerList(question).forEach((answer) => {
      answerDemographicSegments(answer, attributeKey).forEach((segment) => {
        const label = normalisedSegmentLabel(attributeKey, segment);
        const count = scopeCount(segment, scope);
        questionTotals.set(label, (questionTotals.get(label) ?? 0) + count);
      });
    });
    questionTotals.forEach((count, label) => {
      maxNByLabel.set(label, Math.max(maxNByLabel.get(label) ?? 0, count));
    });
  });

  return [...maxNByLabel.entries()]
    .map(([label, n]) => ({ label, n }))
    .sort((left, right) => compareSegmentLabels(attributeKey, left.label, right.label));
}

function compareSegmentLabels(attributeKey: string, left: string, right: string): number {
  if (isAgeAttribute(attributeKey)) {
    const indexLeft = AGE_GROUP_LABELS.indexOf(left as typeof AGE_GROUP_LABELS[number]);
    const indexRight = AGE_GROUP_LABELS.indexOf(right as typeof AGE_GROUP_LABELS[number]);
    const rankLeft = indexLeft === -1 ? Number.MAX_SAFE_INTEGER : indexLeft;
    const rankRight = indexRight === -1 ? Number.MAX_SAFE_INTEGER : indexRight;
    if (rankLeft !== rankRight) {
      return rankLeft - rankRight;
    }
  }
  return left.localeCompare(right);
}

export function listBreakoutAttributes(
  stats: SurveyStats | undefined,
  scope: RespondentScope
): Array<{ key: string; label: string }> {
  if (!stats) {
    return [];
  }

  return [...collectAttributeKeys(stats)]
    .filter((attributeKey) => distinctNonZeroSegmentLabels(stats, attributeKey, scope).size >= 2)
    .sort(compareAttributeOrder)
    .map((key) => ({ key, label: demographicAttributeLabel(key) }));
}

function findSegmentCount(
  answer: AnswerStats,
  attributeKey: string,
  segmentLabelValue: string,
  scope: RespondentScope
): number {
  const segment = answerDemographicSegments(answer, attributeKey)
    .find((entry) => normalisedSegmentLabel(attributeKey, entry) === segmentLabelValue);
  return segment ? scopeCount(segment, scope) : 0;
}

function collectQuestionSegmentLabels(
  question: QuestionStats,
  attributeKey: string,
  scope: RespondentScope
): string[] {
  const labels = new Set<string>();
  answerList(question).forEach((answer) => {
    answerDemographicSegments(answer, attributeKey).forEach((segment) => {
      if (scopeCount(segment, scope) > 0) {
        labels.add(normalisedSegmentLabel(attributeKey, segment));
      }
    });
  });
  return [...labels];
}

export function buildQuestionBreakout(
  question: CombinedQuestionStats,
  attributeKey: string,
  scope: RespondentScope,
  surveySegments?: Array<{ label: string; n: number }>
): BreakoutModel {
  const attributeLabel = demographicAttributeLabel(attributeKey);

  if (question.questionType === SurveyQuestionType.Freeform) {
    return {
      attributeKey,
      attributeLabel,
      segments: [],
      rows: [],
      hasData: false,
      skipReason: 'freeform',
    };
  }

  const questionLabels = collectQuestionSegmentLabels(question, attributeKey, scope)
    .sort((left, right) => compareSegmentLabels(attributeKey, left, right));
  const segmentLabels = surveySegments && surveySegments.length > 0
    ? surveySegments.map((segment) => segment.label)
    : questionLabels;
  const surveyNByLabel = new Map((surveySegments ?? []).map((segment) => [segment.label, segment.n]));

  if (segmentLabels.length === 0) {
    return {
      attributeKey,
      attributeLabel,
      segments: [],
      rows: [],
      hasData: false,
      skipReason: 'no-attribute-data',
    };
  }

  const segmentTotals = new Map<string, number>();
  segmentLabels.forEach((label) => {
    const total = question.answers.reduce(
      (sum, answer) => sum + findSegmentCount(answer, attributeKey, label, scope),
      0
    );
    segmentTotals.set(label, total);
  });

  const segments: BreakoutSegment[] = segmentLabels.map((label) => {
    const n = segmentTotals.get(label) ?? 0;
    const surveyN = surveyNByLabel.get(label) ?? n;
    const key = segmentKeyFromLabel(label);
    return {
      key,
      label,
      n,
      suppressed: surveyN < MIN_SEGMENT_N,
    };
  });

  const activeSegments = segments.filter((segment) => !segment.suppressed);
  const rows: BreakoutRow[] = question.answers.map((answer) => {
    const row: BreakoutRow = {
      answerId: answer.id,
      answerLabel: answer.displayName || answer.title,
      answerImageUrl: answer.imageUrl,
    };

    activeSegments.forEach((segment) => {
      const count = findSegmentCount(answer, attributeKey, segment.label, scope);
      const columnTotal = question.answers.reduce(
        (sum, candidate) => sum + findSegmentCount(candidate, attributeKey, segment.label, scope),
        0
      );
      const pct = columnTotal > 0 ? (count / columnTotal) * 100 : 0;
      row[segment.key] = pct;
      row[`${segment.key}__n`] = count;
    });

    return row;
  });

  return {
    attributeKey,
    attributeLabel,
    segments,
    rows,
    hasData: activeSegments.length > 0 && rows.length > 0,
  };
}

export interface DemographicCompositionSegment {
  key: string;
  label: string;
  count: number;
}

export function buildDemographicComposition(
  stats: SurveyStats,
  attributeKey: string,
  scope: RespondentScope
): DemographicCompositionSegment[] {
  const questionTotals: Array<Map<string, { label: string; count: number }>> = [];

  stats.questions.forEach((question) => {
    const totals = new Map<string, { label: string; count: number }>();
    answerList(question).forEach((answer) => {
      answerDemographicSegments(answer, attributeKey).forEach((segment) => {
        const count = scopeCount(segment, scope);
        if (count <= 0) {
          return;
        }
        const label = normalisedSegmentLabel(attributeKey, segment);
        const key = segmentKeyFromLabel(label);
        const existing = totals.get(key);
        if (existing) {
          existing.count += count;
        } else {
          totals.set(key, { label, count });
        }
      });
    });
    if (totals.size > 0) {
      questionTotals.push(totals);
    }
  });

  const representative = questionTotals.reduce<Map<string, { label: string; count: number }> | null>((best, current) => {
    if (!best) {
      return current;
    }
    const bestSum = [...best.values()].reduce((sum, item) => sum + item.count, 0);
    const currentSum = [...current.values()].reduce((sum, item) => sum + item.count, 0);
    return currentSum > bestSum ? current : best;
  }, null);

  if (!representative) {
    return [];
  }

  return [...representative.values()]
    .map((value) => ({
      key: segmentKeyFromLabel(value.label),
      label: value.label,
      count: value.count,
    }))
    .sort((left, right) => {
      const byBucket = compareSegmentLabels(attributeKey, left.label, right.label);
      if (isAgeAttribute(attributeKey)) {
        return byBucket;
      }
      return right.count - left.count || byBucket;
    });
}

export interface SurveyUserBucketStats {
  total?: number;
  started?: number;
  completed?: number;
  raw_n?: number;
  effective_n?: number | null;
  icc?: number | null;
}

export interface ExtendedSurveyStats extends SurveyStats {
  ai_users?: SurveyUserBucketStats;
  human_users?: SurveyUserBucketStats;
}

export function getSurveyUserBucket(
  stats: SurveyStats,
  bucket: 'ai_users' | 'human_users'
): SurveyUserBucketStats | undefined {
  return (stats as ExtendedSurveyStats)[bucket];
}

export function getRespondentScopeVisibility(stats: SurveyStats): {
  showAi: boolean;
  showHuman: boolean;
  showScopeControl: boolean;
} {
  const aiStarted = getSurveyUserBucket(stats, 'ai_users')?.started ?? stats.aiUsers ?? 0;
  const humanStarted = getSurveyUserBucket(stats, 'human_users')?.started ?? stats.humanUsers ?? 0;
  const showAi = aiStarted > 0;
  const showHuman = humanStarted > 0;
  return {
    showAi,
    showHuman,
    showScopeControl: showAi && showHuman,
  };
}
